import React, { useEffect, useMemo, useState } from "react";

const decodeHtml = (value) => {
    if (!value) {
        return "";
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
};

const stripHtml = (value) => {
    if (!value) {
        return "";
    }
    const normalized = String(value)
        .replace(/<\s*br\s*\/?>/gi, "\n")
        .replace(/<\/\s*p\s*>/gi, "\n\n")
        .replace(/<\/\s*div\s*>/gi, "\n\n")
        .replace(/<\/\s*li\s*>/gi, "\n")
        .replace(/<\/\s*h\d\s*>/gi, "\n\n");
    const stripped = normalized.replace(/<[^>]+>/g, "");
    const text = decodeHtml(stripped);
    return text
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
};

const resolveImageUrl = (url, baseUrl) => {
    if (!url) {
        return "";
    }
    if (url.startsWith("http")) {
        return url;
    }
    const base = (baseUrl || process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    if (url.startsWith("/")) {
        return `${base}${url}`;
    }
    return `${base}/${url}`;
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit"
    }).format(parsed);
};

const normalizeTime = (value) => {
    if (!value) {
        return "";
    }
    const trimmed = String(value).trim();
    if (!trimmed || trimmed.toLowerCase() === "null" || trimmed.toLowerCase() === "undefined") {
        return "";
    }
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) {
        return "";
    }
    const hours = match[1].padStart(2, "0");
    return `${hours}:${match[2]}`;
};

const formatDuration = (startTime, endTime) => {
    const start = normalizeTime(startTime);
    const end = normalizeTime(endTime);
    if (!start) {
        return "";
    }
    if (end && end !== start) {
        return `${start} - ${end}`;
    }
    return start;
};

const pickPrimaryImage = (event) => {
    return (
        event.cover_image_url ||
        event.image_url ||
        (event.gallery_image_urls || [])[0] ||
        ""
    );
};

const LOGO_URL = "/screen.png";
const DESCRIPTION_SCROLL_THRESHOLD = 260;

const buildMetaRows = (event) => {
    const rows = [];
    const dateLine = formatDate(event.date);
    const timeLine = formatDuration(event.time, event.end_time);
    if (dateLine || timeLine) {
        rows.push(`${dateLine}${timeLine ? ` · ${timeLine}` : ""}`.trim());
    }
    const doorTime = normalizeTime(event.door_time);
    if (doorTime) {
        rows.push(`Einlass ${doorTime}`);
    }
    if (event.category) {
        rows.push(stripHtml(event.category));
    }
    if (event.admission) {
        rows.push(`Eintritt ${event.admission}`);
    }
    if (event.sold_out) {
        rows.push("Ausverkauft");
    }
    if (event.is_kultur_for_all) {
        rows.push("Kultur fuer alle");
    }
    return rows;
};

const ScreenShow = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [lastIndex, setLastIndex] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);
    const [apiBase, setApiBase] = useState(
        (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "")
    );

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true);
                const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
                const query = window.location.search || "";
                const response = await fetch(`${base}/api/public/screen/events${query}`);
                if (!response.ok) {
                    throw new Error("Failed to load events");
                }
                const data = await response.json();
                setApiBase(new URL(response.url).origin);
                setEvents(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Failed to load screen events:", error);
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };
        fetchEvents();
    }, []);

    const sortedEvents = useMemo(() => {
        const items = [...events];
        items.sort((a, b) => {
            const aKey = `${a.date || ""} ${a.time || ""}`.trim();
            const bKey = `${b.date || ""} ${b.time || ""}`.trim();
            return aKey.localeCompare(bKey);
        });
        return items;
    }, [events]);

    useEffect(() => {
        if (initialized || sortedEvents.length === 0) {
            return;
        }
        const todayKey = new Date().toISOString().slice(0, 10);
        const nextIndex = sortedEvents.findIndex((event) => (event.date || "") >= todayKey);
        if (nextIndex > 0) {
            setActiveIndex(nextIndex);
        }
        setInitialized(true);
    }, [initialized, sortedEvents]);

    useEffect(() => {
        if (sortedEvents.length <= 1) {
            return undefined;
        }
        const interval = setInterval(() => {
            setActiveIndex((prev) => {
                setLastIndex(prev);
                return (prev + 1) % sortedEvents.length;
            });
        }, 10000);
        return () => clearInterval(interval);
    }, [sortedEvents.length]);

    useEffect(() => {
        const images = sortedEvents
            .flatMap((event) => {
                const list = [
                    event.cover_image_url,
                    event.image_url,
                    ...(event.gallery_image_urls || [])
                ];
                return list.map((item) => resolveImageUrl(item, apiBase)).filter(Boolean);
            })
            .filter(Boolean);
        images.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, [sortedEvents]);

    const activeEvent = sortedEvents[activeIndex];
    const lastEvent = lastIndex !== null ? sortedEvents[lastIndex] : null;
    const activeImages = useMemo(() => {
        if (!activeEvent) {
            return [];
        }
        const list = [
            activeEvent.cover_image_url,
            activeEvent.image_url,
            ...(activeEvent.gallery_image_urls || [])
        ];
        const resolved = list
            .map((item) => resolveImageUrl(item, apiBase))
            .filter(Boolean);
        return Array.from(new Set(resolved));
    }, [activeEvent, apiBase]);

    useEffect(() => {
        setImageIndex(0);
    }, [activeIndex, activeEvent?.id]);

    useEffect(() => {
        if (activeImages.length <= 1) {
            return undefined;
        }
        const interval = setInterval(() => {
            setImageIndex((prev) => (prev + 1) % activeImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [activeImages]);

    if (loading) {
        return (
            <div className="screen-show screen-show--loading">
                <div className="screen-show__loader">
                    <div className="screen-show__spinner" />
                    <p>Show wird vorbereitet...</p>
                </div>
            </div>
        );
    }

    if (!sortedEvents.length) {
        return (
            <div className="screen-show screen-show--empty">
                <div className="screen-show__empty">
                    <h1>Gerade keine Events</h1>
                    <p>Die naechsten Termine erscheinen hier automatisch.</p>
                </div>
            </div>
        );
    }

    const renderSlide = (event, isActive, imageUrl) => {
        if (!event) {
            return null;
        }
        const description = stripHtml(event.description || "");
        const shouldScrollDescription = description.length > DESCRIPTION_SCROLL_THRESHOLD;
        const metaRows = buildMetaRows(event);
        const pricingTiers = event.pricing_tiers || [];
        const title = stripHtml(event.title || "Event");
        const isLongTitle = title.length > 42;

        return (
            <div className={`screen-show__slide ${isActive ? "is-active" : "is-last"}`}>
                <div className="screen-show__image">
                    {imageUrl ? (
                        <img src={imageUrl} alt={title} loading="lazy" />
                    ) : (
                        <div className="screen-show__image-fallback" />
                    )}
                </div>
                <div className="screen-show__overlay" />
                <div className="screen-show__content">
                    <div className={`screen-show__headline ${isLongTitle ? "screen-show__headline--long" : ""}`}>
                        <p className="screen-show__eyebrow">{stripHtml(event.category || "Veranstaltung")}</p>
                        <h1>{title}</h1>
                    </div>
                    <div className="screen-show__details">
                        {metaRows.map((row, index) => (
                            <span key={`${row}-${index}`} className="screen-show__chip">
                                {row}
                            </span>
                        ))}
                    </div>
                    {description && (
                        <div className={`screen-show__description ${shouldScrollDescription ? "screen-show__description--scroll" : ""}`}>
                            <p>{description}</p>
                        </div>
                    )}
                    {pricingTiers.length > 0 && (
                        <div className="screen-show__tiers">
                            {pricingTiers.map((tier, index) => {
                                if (!tier) {
                                    return null;
                                }
                                const label = stripHtml(tier.label || "");
                                const price = tier.price ?? "";
                                const info = stripHtml(tier.info || "");
                                const parts = [label, price, info].filter(Boolean);
                                if (!parts.length) {
                                    return null;
                                }
                                return (
                                    <div key={`${label}-${index}`} className="screen-show__tier">
                                        {parts.join(" · ")}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {(event.ticket_url || event.homepage_url || event.wordpress_url) && (
                        <div className="screen-show__links">
                            {event.ticket_url && (
                                <span>Tickets: {event.ticket_url}</span>
                            )}
                            {event.homepage_url && (
                                <span>Homepage: {event.homepage_url}</span>
                            )}
                            {event.wordpress_url && (
                                <span>Mehr Info: {event.wordpress_url}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="screen-show">
            <div className="screen-show__brand">
                <img src={LOGO_URL} alt="KuBu" />
            </div>
            <div className="screen-show__stage">
                {renderSlide(lastEvent, false, lastEvent ? resolveImageUrl(pickPrimaryImage(lastEvent), apiBase) : "")}
                {renderSlide(activeEvent, true, activeImages[imageIndex] || resolveImageUrl(pickPrimaryImage(activeEvent), apiBase))}
            </div>
            <div className="screen-show__progress">
                {sortedEvents.map((event, index) => (
                    <span
                        key={event.id || `${event.title}-${index}`}
                        className={`screen-show__dot ${index === activeIndex ? "is-active" : ""}`}
                    />
                ))}
            </div>
            {activeImages.length > 1 && (
                <div className="screen-show__image-progress">
                    {activeImages.map((image, index) => (
                        <span
                            key={`${image}-${index}`}
                            className={`screen-show__image-dot ${index === imageIndex ? "is-active" : ""}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ScreenShow;
